import { format } from "date-fns";

import { GoalBasedFinancialPlanDateId } from "@fartherfinance/frontend/api/FinancialPlans/ids";
import { GoalBasedFinancialPlanRetirementHorizonResponse } from "@fartherfinance/frontend/api/FinancialPlans/retirementHorizon/types";
import {
  GoalBasedPlanDateRequest,
  GoalBasedRepeatTimeFrame,
} from "@fartherfinance/frontend/api/FinancialPlans/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import {
  AlreadyStartedOption,
  CalendarYearOption,
  EndOfSpousesPlanningHorizonOption,
  EndOfYourPlanningHorizonOption,
  SpousesAgeOption,
  SpousesRetirementOption,
  YourAgeOption,
  YourRetirementOption,
} from "../constants";
import { PlanDateDropdownValueEnum } from "../Types";

interface Params {
  clientId: ClientId;
  planDateId?: GoalBasedFinancialPlanDateId;
  targetDate?: Date;
  value: PlanDateDropdownValueEnum;
  calendarYear?: string;
  yourAge?: string;
  spousesAge?: string;
  retirementHorizons: GoalBasedFinancialPlanRetirementHorizonResponse[];
  repeats: GoalBasedRepeatTimeFrame;
}

export const generatePlanDateFromFormValues = ({
  clientId,
  planDateId,
  targetDate,
  value,
  calendarYear,
  yourAge,
  spousesAge,
  retirementHorizons,
  repeats,
}: Params): GoalBasedPlanDateRequest => {
  const yourRetirementHorizon = retirementHorizons.find(
    (rh) => rh.owner === clientId
  );

  const spousesRetirementHorizon = retirementHorizons.find(
    (rh) => rh.owner !== clientId
  );

  if (repeats === "None" && targetDate !== undefined) {
    return {
      owner: clientId,
      planDateId,
      value: format(targetDate, "yyyy-MM-dd"),
      dateType: "DateString",
    };
  }

  if (value === AlreadyStartedOption.value) {
    return {
      owner: clientId,
      planDateId,
      value: "",
      dateType: "AlreadyStarted",
    };
  }

  if (value === CalendarYearOption.value) {
    if (calendarYear === undefined) {
      throw new Error("Calendar Year is undefined");
    }

    return {
      owner: clientId,
      planDateId,
      value: calendarYear,
      dateType: "Year",
    };
  }

  if (value === YourAgeOption.value) {
    if (yourAge === undefined) {
      throw new Error("Your Age is undefined");
    }

    return {
      owner: clientId,
      planDateId,
      value: yourAge,
      dateType: "Age",
    };
  }

  if (value === SpousesAgeOption.value) {
    if (spousesAge === undefined) {
      throw new Error("Spouse's Age is undefined");
    }

    if (spousesRetirementHorizon === undefined) {
      throw new Error("Spousal ClientId is required");
    }

    return {
      owner: spousesRetirementHorizon.owner,
      planDateId,
      value: spousesAge,
      dateType: "Age",
    };
  }

  if (retirementHorizons === undefined) {
    throw new Error("retirementHorizons is undefined");
  }

  if (yourRetirementHorizon === undefined) {
    throw new Error("yourRetirementHorizon is undefined");
  }

  if (value === YourRetirementOption.value) {
    const yourRetirement = retirementHorizons.find(
      (rh) => rh.owner === clientId
    );
    if (yourRetirement === undefined) {
      throw new Error("Could not find clientId in retirementHorizon[]");
    }

    if (yourRetirementHorizon.startDate === undefined) {
      throw new Error("retirementHorizon.startDate is undefined for clientId");
    }

    return yourRetirementHorizon.startDate;
  }

  if (value === EndOfYourPlanningHorizonOption.value) {
    const yourPlanningHorizon = retirementHorizons.find(
      (rh) => rh.owner === clientId
    );
    if (yourPlanningHorizon === undefined) {
      throw new Error("Could not find clientId in retirementHorizon[]");
    }

    if (yourRetirementHorizon.endDate === undefined) {
      throw new Error("retirementHorizon.endDate is undefined for clientId");
    }

    return yourRetirementHorizon.endDate;
  }

  if (spousesRetirementHorizon === undefined) {
    throw new Error("spousesRetirementHorizon is undefined");
  }

  if (value === SpousesRetirementOption.value) {
    const spousesRetirement = retirementHorizons.find(
      (rh) => rh.owner !== clientId
    );
    if (spousesRetirement === undefined) {
      throw new Error("Could not find non-clientId in retirementHorizon[]");
    }

    if (spousesRetirementHorizon.startDate === undefined) {
      throw new Error(
        "retirementHorizon.startDate is undefined for non-clientId"
      );
    }

    return spousesRetirementHorizon.startDate;
  }

  if (value === EndOfSpousesPlanningHorizonOption.value) {
    const spousesPlanningHorizon = retirementHorizons.find(
      (rh) => rh.owner !== clientId
    );
    if (spousesPlanningHorizon === undefined) {
      throw new Error("Could not find non-clientId in retirementHorizon[]");
    }

    if (spousesRetirementHorizon.endDate === undefined) {
      throw new Error(
        "retirementHorizon.endDate is undefined for non-clientId"
      );
    }

    return spousesRetirementHorizon.endDate;
  }

  throw new Error("Should not reach this point in getValueFromPlanDate.ts");
};
